import { RfxQuoteHeader } from "../../../newui/features/Rfx/Submission/components/QuoteTab/RfxQuoteHeader";
import type { RfxQuoteTable } from "../../../newui/features/Rfx/Submission/types/quote.type";
import { FILE_UPLOAD_TYPES, FileUpload, UploadedFile } from "newui/components/FileUpload";
import { convertUploadedFileToFileAbleFile } from "newui/components/FileUpload/utils";
import { UploadStatus } from "newui/components/FileUpload/types/UploadStatus.type";
import type { FileableFile } from "types/storage.types";

export interface FlexibleQuoteFileProps {
	clientName?: string;
	disabled?: boolean;
	flexibleQuoteSavedFiles?: FileableFile[] | null;
	isPreview?: boolean;
	rfqId?: number;
	rfxInviteId?: number | null;
	/**
	 * Called with the current, fully-uploaded file list whenever it changes
	 * (add/delete). Lets the parent keep its own copy in sync so a later
	 * unmount/remount (tab switch) reseeds `flexibleQuoteSavedFiles` from
	 * current data instead of the stale value it had at initial load.
	 */
	onChange?: (files: FileableFile[]) => void;
}

export const FlexibleQuoteFile = ({
	rfqId,
	rfxInviteId,
	clientName,
	disabled = false,
	flexibleQuoteSavedFiles,
	isPreview = false,
	onChange,
}: FlexibleQuoteFileProps) => {
	const handleChange = (files: UploadedFile[]) => {
		const uploaded = files
			.filter((f) => f.status === UploadStatus.UPLOADED && f.id && f.path)
			.map((f) => convertUploadedFileToFileAbleFile(f) as FileableFile);
		onChange?.(uploaded);
	};

	return (
		<RfxQuoteHeader
			title="Alternative Quote"
			description={`${clientName} has allowed you to provide an alternative quote upload, if required.`}
			// `RfxQuoteHeader` is shared with the revisable-quote-table UI, where
			// `quote` drives its revise/compare actions. This alternative-quote
			// upload has no such concept, and neither `onOpenReviseQuote` nor
			// `onCompareRevisions` is passed, so `quote` is never read here.
			quote={{} as RfxQuoteTable}
		>
			{disabled && !flexibleQuoteSavedFiles?.length ? (
				<div className="my-4 text-gray-500">
					No alternative quote or supporting documentation submitted.
				</div>
			) : (
				<FileUpload
					allowFileTypes={["xlsx", "xls", "csv", "pdf"]}
					multiple
					maxFileSizeMB={100}
					disabled={disabled}
					isPreview={isPreview}
					initialValue={flexibleQuoteSavedFiles ?? []}
					uploadType={FILE_UPLOAD_TYPES.FLEXIBLE_QUOTE_FILE_RESPONSE}
					extraData={{
						rfq_id: rfqId,
						rfxInviteId: rfxInviteId,
					}}
					supportsHardDelete
					onChange={handleChange}
				/>
			)}
		</RfxQuoteHeader>
	);
};
